<template>
  <div class="indoor-marker" ref="container" :style="containerStyle" @mousedown="onContainerMouseDown">
    <!-- 背景图 -->
    <img v-if="backgroundImage && backgroundImage.url"
         ref="bgImg"
         :src="backgroundImage.url"
         class="indoor-marker-bg"
         :style="bgStyle"
         @load="onImageLoad"
         draggable="false" />
    <!-- 标注块 -->
    <div v-for="block in blocks"
         :key="block.id"
         class="indoor-marker-block"
         :class="'indoor-marker-block-' + block.type"
         :style="getBlockStyle(block)"
         @mousedown.stop.prevent="onBlockMouseDown($event, block); onBlockMouseDownRecord($event, block)"
         @click.stop="$emit('blockClick', block)"
         @dblclick.stop="onBlockDblClick(block)">
      <span v-if="block.icon" class="indoor-marker-icon-wrap">
        <svg-icon :name="block.icon" :size="20" color="#fff" class="indoor-marker-svg" />
      </span>
      <span v-else class="indoor-marker-dot">＋</span>
      <!-- <span v-if="block.name" class="indoor-marker-name">{{ block.name }}</span> -->
    </div>
  </div>
</template>

<script>
let blockIdCounter = 0

export default {
  name: 'IndoorMarker',
  emits: ['dragEnd', 'blockClick'],
  props: {
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: 'auto'
    },
    value: {
      type: Array,
      default: () => []
    },
    backgroundImage: {
      type: Object,
      default: () => ({})
    },
    blockImages: {
      type: Object,
      default: () => ({})
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      blocks: [],
      dragging: null,      // { block, offsetX, offsetY }
      imgNaturalWidth: 0,
      imgNaturalHeight: 0,
      iconMap: {
        fire: '🧯',
        video: '📹',
        exit: '🚪',
        water: '💧'
      }
    }
  },
  computed: {
    containerStyle() {
      return {
        width: this.width,
        height: this.height,
        position: 'relative',
        overflow: 'hidden',
        userSelect: 'none',
        border: '1px solid #e5e6eb',
        borderRadius: '4px',
        minHeight: '200px'
      }
    },
    bgStyle() {
      return {
        width: this.backgroundImage.width || '100%',
        height: this.backgroundImage.height || 'auto',
        display: 'block',
        pointerEvents: 'none'
      }
    }
  },
  watch: {
    value: {
      handler(val) {
        this.blocks = (val || []).map(b => ({
          ...b,
          id: b.id || ('block-' + (++blockIdCounter))
        }))
      },
      immediate: true,
      deep: true
    }
  },
  beforeUnmount() {
    this.unbindGlobalEvents()
  },
  methods: {
    onImageLoad(e) {
      this.imgNaturalWidth = e.target.naturalWidth
      this.imgNaturalHeight = e.target.naturalHeight
    },

    getBlockIcon(block) {
      return this.blockImages[block.type || block.id_devicetype] || ''
    },
    isEmoji(str) {
      if (!str) return false
      return /^[\u{1F000}-\u{1FFFF}]|\p{Emoji}/u.test(str) || str.length <= 2
    },
    getBlockStyle(block) {
      return {
        left: block.x + '%',
        top: block.y + '%',
        position: 'absolute',
        transform: 'translate(-50%, -100%)',
        cursor: 'move',
        zIndex: this.dragging && this.dragging.block.id === block.id ? 10 : 1
      }
    },

    // 添加标注图标
    addBlockLocalIcon(type) {
      const id = 'block-' + (++blockIdCounter)
      // 默认放在图片中央偏随机位置，避免完全重叠
      const x = 40 + Math.random() * 20
      const y = 40 + Math.random() * 20
      this.blocks.push({ id, type, x, y })
    },

    // 获取布局信息
    getLayoutInfo() {
      return {
        blocks: this.blocks.map(b => ({
          type: b.type,
          x: Math.round(b.x * 100) / 100,
          y: Math.round(b.y * 100) / 100
        }))
      }
    },

    // 鼠标按下：开始拖拽标注块
    onBlockMouseDown(e, block) {
      if (this.readonly) return
      e.preventDefault()
      const containerRect = this.$refs.container.getBoundingClientRect()
      const blockX = block.x / 100 * containerRect.width
      const blockY = block.y / 100 * containerRect.height

      this.dragging = {
        block,
        offsetX: e.clientX - blockX,
        offsetY: e.clientY - blockY
      }
      this.bindGlobalEvents()
    },

    // 鼠标按下容器空白区域：不做操作（阻止穿透）
    onContainerMouseDown() {},

    // 全局鼠标移动
    onGlobalMouseMove(e) {
      if (!this.dragging) return
      const containerRect = this.$refs.container.getBoundingClientRect()
      let x = ((e.clientX - this.dragging.offsetX) / containerRect.width) * 100
      let y = ((e.clientY - this.dragging.offsetY) / containerRect.height) * 100

      // 限制范围
      x = Math.max(0, Math.min(100, x))
      y = Math.max(0, Math.min(100, y))

      this.dragging.block.x = x
      this.dragging.block.y = y
    },

    // 全局鼠标松开：结束拖拽
    onGlobalMouseUp() {
      const wasDragging = this.dragging
      const block = this.dragging ? this.dragging.block : null
      const moved = wasDragging && this._dragStartX !== block.x && this._dragStartY !== block.y
      this.dragging = null
      this.unbindGlobalEvents()
      if (wasDragging) {
        if (moved) {
          this.$emit('dragEnd', block)
        }
        // 不管有没有移动，给个点击机会（与点击区分）
      }
    },
    // 记录拖拽起始位置
    onBlockMouseDownRecord(e, block) {
      this._dragStartX = block.x
      this._dragStartY = block.y
    },

    // 双击删除标注
    onBlockDblClick(block) {
      const idx = this.blocks.findIndex(b => b.id === block.id)
      if (idx !== -1) {
        this.blocks.splice(idx, 1)
      }
    },

    bindGlobalEvents() {
      window.addEventListener('mousemove', this.onGlobalMouseMove)
      window.addEventListener('mouseup', this.onGlobalMouseUp)
    },

    unbindGlobalEvents() {
      window.removeEventListener('mousemove', this.onGlobalMouseMove)
      window.removeEventListener('mouseup', this.onGlobalMouseUp)
    }
  }
}
</script>

<style scoped>
.indoor-marker {
  background: #f7f8fa;
}

.indoor-marker-bg {
  user-select: none;
  -webkit-user-drag: none;
}

.indoor-marker-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: move;
  transition: filter 0.15s;
}

.indoor-marker-block:hover {
  filter: brightness(1.15);
}

.indoor-marker-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #e507f0;
  color: #fff;
  border-radius: 50%;
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
  box-shadow: 0 0 0 3px rgba(0,144,255,0.3), 0 2px 6px rgba(0,0,0,0.2);
}

.indoor-marker-name {
  font-size: 11px;
  color: #1d2129;
  background: rgba(255,255,255,0.92);
  padding: 1px 5px;
  border-radius: 2px;
  white-space: nowrap;
  margin-top: 2px;
  text-shadow: 0 0 2px #fff;
}

.indoor-marker-icon {
  line-height: 1;
}

.indoor-marker-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1677ff, #4096ff);
  box-shadow: 0 2px 12px rgba(22,119,255,0.4), 0 0 0 4px rgba(22,119,255,0.15);
  transition: transform 0.15s, box-shadow 0.15s;
}

.indoor-marker-block:hover .indoor-marker-icon-wrap {
  transform: scale(1.1);
  box-shadow: 0 4px 16px rgba(22,119,255,0.5), 0 0 0 6px rgba(22,119,255,0.2);
}

.indoor-marker-svg {
  width: 20px;
  height: 20px;
}

.indoor-marker-img {
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.25);
}
</style>
